import {
  averageSpeed,
  closingRob,
  formatPosition,
  freshWaterClosing,
  parsePosition,
  slipPanel,
  slipPercent,
  steamingHours,
  theoreticalSpeed,
} from '@felix/domain';
import type { VoyageReport } from './types.js';

/**
 * Noon Report ekranının saf yardımcıları (ADR 0022 §4): konum biçimi, saat farkı seçenekleri,
 * varsayılan rapor zamanı, önceki rapordan açılış değerleri ve hesaplanan alanlar. Hesapların
 * kendisi `@felix/domain`'de; ekran yalnız çağırır.
 */

const round = (value: number, digits = 1) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

/** 34.76 → "34° 45.6' N" (ekrandaki biçim; ADR 0054 §3). */
export const formatLatitude = (lat: number | null) => formatPosition(lat, 'lat');
/** 58.39 → "058° 23.4' E". */
export const formatLongitude = (lon: number | null) => formatPosition(lon, 'lon');

/** "34° 45.6' N" ya da "34 45.6 N" → ondalık derece; biçimsizse `null`. */
export const parseLatitude = (text: string) => parsePosition(text, 'lat');
export const parseLongitude = (text: string) => parsePosition(text, 'lon');

/**
 * Listede hazır saat farkları (dakika): 30 dakikalık adım, −12:00…+14:00 (Noon Report "Time Zone").
 * Fark dakika hassasiyetindedir; adıma uymayan mevcut değer `offsetChoices` ile eklenir (ADR 0065 §8).
 */
export const TZ_OFFSETS: readonly number[] = Array.from({ length: 53 }, (_, i) => -720 + i * 30);

export function formatTzOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC ${sign}${hh}:${mm}`;
}

/** Dar yerler (başlık kartı) için iki satır: yerel saat ve fark (`UTC +03:00`). */
export function plannedTimeParts(
  iso: string | null | undefined,
  tzOffsetMin: number | null | undefined,
): { stamp: string; zone: string } {
  const full = formatPlannedTime(iso, tzOffsetMin);
  const open = full.lastIndexOf(' (');
  return open === -1
    ? { stamp: full, zone: '' }
    : { stamp: full.slice(0, open), zone: full.slice(open + 2, -1) };
}

/** Başlık kartının olgusu: değer yerel saat, altında fark (`HeadFact`). */
export function plannedFact(
  label: string,
  iso: string | null | undefined,
  tzOffsetMin: number | null | undefined,
): { label: string; value: string; sub?: string } {
  const { stamp, zone } = plannedTimeParts(iso, tzOffsetMin);
  return zone ? { label, value: stamp, sub: zone } : { label, value: stamp };
}

/**
 * Planlanan zaman (ADR 0054 §2): limanın yerel saati ve farkı, `14.10.2026 20:37 (UTC +03:00)`.
 * Farkı olmayan eski seferde UTC; zaman yoksa `—`.
 */
export function formatPlannedTime(
  iso: string | null | undefined,
  tzOffsetMin: number | null | undefined,
): string {
  if (!iso) return '—';
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '—';
  const offset = tzOffsetMin ?? 0;
  const local = new Date(at + offset * 60_000).toISOString();
  const stamp = `${local.slice(8, 10)}.${local.slice(5, 7)}.${local.slice(0, 4)} ${local.slice(11, 16)}`;
  return tzOffsetMin === null || tzOffsetMin === undefined
    ? `${stamp} (UTC)`
    : `${stamp} (${formatTzOffset(offset)})`;
}

/**
 * Varsayılan rapor zamanı: gemi saatiyle bugün 12:00; henüz gelmediyse şimdi. Gelecek zamanlı
 * rapor sunucuda reddedilir (`FUTURE_DATE`).
 */
export function defaultReportTime(now: Date, tzOffsetMin: number): string {
  const local = new Date(now.getTime() + tzOffsetMin * 60_000);
  const noonLocal = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), 12);
  const noon = noonLocal - tzOffsetMin * 60_000;
  const minute = Math.floor(now.getTime() / 60_000) * 60_000;
  return new Date(Math.min(noon, minute)).toISOString();
}

/** Gemi saatiyle o günün 12:00'ı (UTC an). */
function localNoon(at: number, tzOffsetMin: number): number {
  const local = new Date(at + tzOffsetMin * 60_000);
  return (
    Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), 12) -
    tzOffsetMin * 60_000
  );
}

const shipDay = (at: number, tzOffsetMin: number) =>
  new Date(at + tzOffsetMin * 60_000).toISOString().slice(0, 10);

/** Gemi saatiyle 12:00'dan önce mi (ADR 0157 §6, #2272): erken açılan Noon'un bilgi şeridi. */
export function earlyNoon(now: Date, tzOffsetMin: number): boolean {
  return now.getTime() < localNoon(now.getTime(), tzOffsetMin);
}

/**
 * Erken Noon şeridi (ADR 0157 §6, #2278): 12:00 LT'den önce açılan yeni Noon'da kayıt anı yeniden
 * hesaplanır ("şimdi kaydederseniz ..."); var olan taslağın saati kayıtta değişmez, şerit taslağın
 * saatini söyler. Gönderilmiş raporda ya da 12:00 geçtiyse `null`.
 */
export function earlyNoonNotice(
  report: { status: string; reportedAt: string } | null,
  now: Date,
  tzOffsetMin: number,
): { kind: 'new' } | { kind: 'draft'; reportedAt: string } | null {
  if (!earlyNoon(now, tzOffsetMin)) return null;
  if (!report) return { kind: 'new' };
  return report.status === 'draft' || report.status === 'reopened'
    ? { kind: 'draft', reportedAt: report.reportedAt }
    : null;
}

/**
 * Saat alanına dokunulmamış Noon'un kayıt anındaki zamanı (ADR 0157 §6): aynı gemi gününde
 * varsayılan yeniden hesaplanır (`defaultReportTime`; 12:00 geçtiyse 12:00). Gün değiştiyse ya da
 * değer aynıysa `null` (dokunulmaz). FUTURE_DATE aynen geçerli: sonuç hiçbir zaman şimdiden sonra
 * değildir.
 */
export function refreshedReportTime(opened: string, now: Date, tzOffsetMin: number): string | null {
  const at = Date.parse(opened);
  if (Number.isNaN(at) || shipDay(at, tzOffsetMin) !== shipDay(now.getTime(), tzOffsetMin)) {
    return null;
  }
  const next = defaultReportTime(now, tzOffsetMin);
  return Date.parse(next) === at ? null : next;
}

/**
 * 12:00'dan önce tarihli Noon taslağı 12:00'dan sonra açıldı: önerilen saat (o günün 12:00'ı).
 * Gönderilmiş raporda öneri yok (yol, kaptanın yeniden açmasıdır).
 */
export function earlyNoonDraft(
  report: { status: string; reportedAt: string; tzOffsetMin: number },
  now: Date,
): string | null {
  if (report.status !== 'draft' && report.status !== 'reopened') return null;
  const at = Date.parse(report.reportedAt);
  if (Number.isNaN(at)) return null;
  const noon = localNoon(at, report.tzOffsetMin);
  return at < noon && now.getTime() >= noon ? new Date(noon).toISOString() : null;
}

/**
 * Sonraki Noon Report zamanı: rapordan sonraki ilk gemi saati 12:00 (aynı saat farkıyla). Yalnız
 * bilgi (Next Report kartı); saat farkı değişirse bir sonraki rapor kendi farkıyla girilir.
 */
export function nextNoonAt(reportedAt: string, tzOffsetMin: number): string {
  const at = Date.parse(reportedAt);
  const local = new Date(at + tzOffsetMin * 60_000);
  let noon =
    Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), 12) -
    tzOffsetMin * 60_000;
  if (noon <= at) noon += 86_400_000;
  return new Date(noon).toISOString();
}

const LEG_KINDS = new Set(['cosp', 'noon']);
const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

/** Seyir süresinin başladığı rapor: verilen zamandan önceki son gönderilmiş COSP ya da Noon. */
export function previousLeg(reports: readonly VoyageReport[], at: string): VoyageReport | null {
  return (
    reports
      .filter(
        (r) =>
          LEG_KINDS.has(r.kind) &&
          r.status === 'submitted' &&
          Date.parse(r.reportedAt) < Date.parse(at),
      )
      .sort(byTime)
      .at(-1) ?? null
  );
}

interface Row {
  opening: number;
  consumption: number;
  bunkered: number;
}
type FuelRow = Row & { fuel: string };
type LubeRow = Row & { name: string };
interface FreshWater {
  rob: number;
  consumption: number;
  produced: number;
  bunkered: number;
}
interface FuelData {
  fuels?: FuelRow[];
  lubes?: LubeRow[] | null;
  freshWater?: FreshWater | null;
}

/** Yeni Noon taslağının yakıt bölümü (açılışlar önceki raporun kapanışından). */
export interface NoonFuelDraft {
  fuels: FuelRow[];
  lubes: LubeRow[];
  freshWater: FreshWater | null;
}

/**
 * Yeni Noon taslağı: açılış ROB'ları bir önceki gönderilmiş Noon'un kapanışından; Noon yoksa
 * COSP ya da Departure ROB'undan (ADR 0022 §4).
 */
export function noonDraft(reports: readonly VoyageReport[], at: string): { fuel: NoonFuelDraft } {
  const earlier = reports
    .filter((r) => r.status === 'submitted' && Date.parse(r.reportedAt) < Date.parse(at))
    .sort(byTime);
  const noon = earlier.filter((r) => r.kind === 'noon').at(-1);
  if (noon) {
    const fuel = (noon.data.fuel ?? {}) as FuelData;
    const reset = { consumption: 0, bunkered: 0 };
    return {
      fuel: {
        fuels: (fuel.fuels ?? []).map((row) => ({
          fuel: row.fuel,
          opening: closingRob(row),
          ...reset,
        })),
        lubes: (fuel.lubes ?? []).map((row) => ({
          name: row.name,
          opening: closingRob(row),
          ...reset,
        })),
        freshWater: fuel.freshWater
          ? { rob: freshWaterClosing(fuel.freshWater), consumption: 0, produced: 0, bunkered: 0 }
          : null,
      },
    };
  }
  const start = earlier.filter((r) => r.kind === 'cosp' || r.kind === 'departure').at(-1);
  const rob = (start?.data.rob ?? []) as { fuel: string; rob: number }[];
  return {
    fuel: {
      fuels: rob.map((row) => ({ fuel: row.fuel, opening: row.rob, consumption: 0, bunkered: 0 })),
      lubes: [],
      freshWater: null,
    },
  };
}

export interface NoonDerived {
  steamingHours: number | null;
  averageSpeedKn: number | null;
  theoreticalSpeedKn: number | null;
  slipPct: number | null;
  /** Slip (SOG) ve akıntı etkisi (ADR 0059 §7); saklanmaz. */
  slipSogPct: number | null;
  currentEffectKn: number | null;
  currentEffectPct: number | null;
  fuels: { fuel: string; closing: number }[];
  lubes: { name: string; closing: number }[];
  freshWaterClosing: number | null;
  totalConsumptionMt: number | null;
  totalRobMt: number | null;
}

const num = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Formdaki değerlerden hesaplanan alanlar; eksik girdide `null`. */
export function noonDerived(
  report: { reportedAt: string; data: Record<string, unknown> },
  previousAt: string | null,
): NoonDerived {
  const navigation = (report.data.navigation ?? {}) as Record<string, unknown>;
  const machinery = (report.data.machinery ?? {}) as {
    mainEngine?: Record<string, unknown>;
    propeller?: Record<string, unknown>;
  };
  const fuel = (report.data.fuel ?? {}) as FuelData;
  const hours = previousAt ? steamingHours(previousAt, report.reportedAt) : null;
  const distance = num(navigation.distanceSinceLast);
  const pitch = num(machinery.propeller?.pitchM);
  // Pervane şaft devriyle döner; şaft devri yoksa makine devri
  const rpm = num(machinery.mainEngine?.shaftRpm) ?? num(machinery.mainEngine?.rpm);
  const theoretical = pitch !== null && rpm !== null ? theoreticalSpeed(pitch, rpm) : null;
  const stw = num(navigation.stw);
  const valid = <T extends Row>(rows: readonly T[] | null | undefined) =>
    (rows ?? []).filter(
      (row) =>
        num(row.opening) !== null && num(row.consumption) !== null && num(row.bunkered) !== null,
    );
  const fuels = valid(fuel.fuels).map((row) => ({ fuel: row.fuel, closing: closingRob(row) }));
  const lubes = valid(fuel.lubes).map((row) => ({ name: row.name, closing: closingRob(row) }));
  const water = fuel.freshWater;
  const waterValid =
    water &&
    [water.rob, water.consumption, water.produced, water.bunkered].every((v) => num(v) !== null);
  const consumption = valid(fuel.fuels).reduce((sum, row) => sum + row.consumption, 0);
  const slip = slipPanel({ pitchM: pitch, rpm, stw, sog: num(navigation.sog) });
  return {
    steamingHours: hours,
    averageSpeedKn: distance !== null ? averageSpeed(distance, hours) : null,
    theoreticalSpeedKn: theoretical,
    slipPct: theoretical !== null && stw !== null ? slipPercent(theoretical, stw) : null,
    slipSogPct: slip.slipSogPct,
    currentEffectKn: slip.currentEffectKn,
    currentEffectPct: slip.currentEffectPct,
    fuels,
    lubes,
    freshWaterClosing: waterValid ? freshWaterClosing(water) : null,
    totalConsumptionMt: fuels.length > 0 ? round(consumption, 3) : null,
    totalRobMt:
      fuels.length > 0
        ? round(
            fuels.reduce((sum, row) => sum + row.closing, 0),
            3,
          )
        : null,
  };
}
